/**
 * Cron Audit — mirrors apps/experts/public/admin/cron-audit.html. GET
 * /api/admin/cron-audit?job=&limit= lists recent executions; the page supports
 * the same optional job filter + limit the endpoint accepts. State is local
 * (controlled inputs), not URL — kept minimal for the rebuild slice.
 */
import { useState } from 'react';
import {
  AdminEmpty,
  AdminError,
  AdminLoading,
  AdminPageHeader,
  Mono,
  Panel,
  StatusDot,
  Table,
  Td,
  Th,
  toneForStatus,
  Tr,
} from '../../components/primitives';
import { useCronAudit } from '../../data-access/useAdmin';
import { absTime, duration, relTime } from '../../lib/format';

const LIMITS = [25, 50, 100, 200];

export function CronAuditPage() {
  const [jobInput, setJobInput] = useState('');
  const [job, setJob] = useState<string | null>(null);
  const [limit, setLimit] = useState(50);

  const { data, isPending, isError, error, refetch, isFetching } = useCronAudit(
    job,
    limit,
  );

  return (
    <div>
      <AdminPageHeader
        subtitle={
          data
            ? `${data.total} executions · ${relTime(data.generated_at)}`
            : 'Cron execution audit log'
        }
        title="Cron Audit"
      />

      <Panel
        actions={
          <button
            className="rounded-md border border-border-subtle px-3 py-1.5 font-inter text-content-secondary text-sm hover:bg-surface-background"
            onClick={() => void refetch()}
            type="button"
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        }
        title="Filters"
      >
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setJob(jobInput.trim() || null);
          }}
        >
          <label className="flex flex-col gap-1 font-inter text-content-secondary text-xs">
            Job name (exact)
            <input
              className="w-64 rounded-md border border-border-subtle bg-surface-background px-2 py-1.5 font-inter text-content-primary text-sm"
              onChange={(e) => setJobInput(e.target.value)}
              placeholder="caj-andavo-labs-kb-sync-dev"
              type="text"
              value={jobInput}
            />
          </label>
          <label className="flex flex-col gap-1 font-inter text-content-secondary text-xs">
            Limit
            <select
              className="rounded-md border border-border-subtle bg-surface-background px-2 py-1.5 font-inter text-content-primary text-sm"
              onChange={(e) => setLimit(Number(e.target.value))}
              value={limit}
            >
              {LIMITS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <button
            className="rounded-md bg-surface-action-subtle px-3 py-1.5 font-inter text-content-action text-sm hover:opacity-90"
            type="submit"
          >
            Apply
          </button>
        </form>
      </Panel>

      <Panel className="mt-4">
        {isPending ? <AdminLoading /> : null}
        {isError ? (
          <AdminError
            message={`Failed to load: ${(error as Error)?.message ?? 'unknown'}`}
          />
        ) : null}
        {data && data.executions.length === 0 ? (
          <AdminEmpty>No executions recorded.</AdminEmpty>
        ) : null}
        {data && data.executions.length > 0 ? (
          <Table
            head={
              <>
                <Th>Job</Th>
                <Th>Status</Th>
                <Th>Exit</Th>
                <Th>Duration</Th>
                <Th>Started</Th>
                <Th>Summary</Th>
              </>
            }
          >
            {data.executions.map((ex) => (
              <Tr key={ex.id}>
                <Td>
                  <Mono title={ex.job_name}>{ex.job_name}</Mono>
                </Td>
                <Td>
                  <span className="inline-flex items-center gap-1.5">
                    <StatusDot tone={toneForStatus(ex.status)} />
                    {ex.status}
                  </span>
                </Td>
                <Td>{ex.exit_code ?? '—'}</Td>
                <Td>{duration(ex.duration_ms)}</Td>
                <Td title={absTime(ex.started_at)}>{relTime(ex.started_at)}</Td>
                <Td className="max-w-xs truncate text-content-secondary">
                  {ex.output_summary ?? '—'}
                </Td>
              </Tr>
            ))}
          </Table>
        ) : null}
      </Panel>
    </div>
  );
}

export default CronAuditPage;
